// Rhythm model for the notation engraver.
//
// The RhythmValue ordering is deliberately aligned to Guitar Pro 8's internal
// spacing-table indices (reverse-engineered from libGPIO's am::music::Spacing
// and BeamsEngraver::beamLevel) so the extracted constants in spacing.ts line
// up 1:1 by enum value. See SPEC.md §2.

export enum RhythmValue {
  Longa = 0,
  Breve = 1,
  Whole = 2,
  Half = 3,
  Quarter = 4,
  Eighth = 5,
  Sixteenth = 6,
  ThirtySecond = 7,
  SixtyFourth = 8,
  OneTwentyEighth = 9,
  TwoFiftySixth = 10,
}

export interface Rhythm {
  base: RhythmValue;
  /** Augmentation dots (each adds half of the running value). */
  dots: number;
  /**
   * A triplet-family member: the note SOUNDS ⅔ of its written base (triplet
   * eighth = ⅓ quarter, sextuplet sixteenth = ⅙, triplet quarter = ⅔). The
   * written base drives glyph/flag/beam count; the group's "3"/"6" figure is
   * drawn by the engraver.
   */
  triplet?: boolean;
}

/**
 * Duration of a bare RhythmValue in quarter-length units (quarter = 1). We
 * work in quarter-lengths rather than "beats" so the mapping is independent of
 * the time-signature denominator; in 4/4 a quarter-length equals one beat.
 */
export function quarterLengthOf(base: RhythmValue): number {
  // Quarter (index 4) = 1; each step doubles/halves.
  return 2 ** (RhythmValue.Quarter - base);
}

/** Total quarter-lengths of a dotted value: base * (2 - 2^-dots). */
export function rhythmDuration({ base, dots }: Rhythm): number {
  return quarterLengthOf(base) * (2 - 2 ** -dots);
}

const DOT_THRESHOLDS: ReadonlyArray<[number, number]> = [
  [1.875, 3],
  [1.75, 2],
  [1.5, 1],
];

/**
 * Decompose a duration in quarter-lengths into a base note value plus dots.
 * Picks the largest base value that fits, then adds up to 3 dots for the
 * remainder. Tuplets are out of scope here (a later pass handles them).
 */
export function beatsToRhythm(quarterLengths: number): Rhythm {
  if (!(quarterLengths > 0)) {
    throw new Error(`beatsToRhythm: duration must be positive, got ${quarterLengths}`);
  }
  // Triplet family first: a duration of exactly ⅔ × a plain value is a
  // triplet member and takes that value as its WRITTEN base — a triplet
  // eighth (⅓ quarter) is an eighth with one beam, a sextuplet sixteenth
  // (⅙) a sixteenth with two, never the floor()'d next-smaller plain value
  // (which drew sextuplet runs as three-beam 32nd blocks).
  for (let v = RhythmValue.Longa; v <= RhythmValue.TwoFiftySixth; v++) {
    if (Math.abs(quarterLengths - (quarterLengthOf(v) * 2) / 3) < 1e-6) {
      return { base: v, dots: 0, triplet: true };
    }
  }
  // Largest base whose bare duration is <= the requested length.
  let base = RhythmValue.TwoFiftySixth;
  for (let v = RhythmValue.Longa; v <= RhythmValue.TwoFiftySixth; v++) {
    if (quarterLengthOf(v) <= quarterLengths + 1e-9) {
      base = v;
      break;
    }
  }
  const ratio = quarterLengths / quarterLengthOf(base);
  let dots = 0;
  for (const [threshold, n] of DOT_THRESHOLDS) {
    if (ratio + 1e-9 >= threshold) {
      dots = n;
      break;
    }
  }
  return { base, dots };
}

/**
 * Number of beams a stem carries. Verbatim from GP's
 * BeamsEngraver::beamLevel: eighth=1, 16th=2, 32nd=3, 64th=4, 128th=5;
 * quarter and longer carry none.
 */
export function beamLevel(v: RhythmValue): number {
  return v >= RhythmValue.Eighth && v <= RhythmValue.OneTwentyEighth ? v - RhythmValue.Quarter : 0;
}

/** Whether a value is beamed/flagged at all (eighth or shorter). */
export function isBeamable(v: RhythmValue): boolean {
  return beamLevel(v) > 0;
}
